<template lang="pug">
v-card.pa-6.mb-6.rounded-lg(outlined)
  .text-md.font-semibold.neutral700--text.mb-4 Order information
  v-row(no-gutters)
    v-col.pr-5(:cols="isQuotationOrStockTransfer ? 3 : 4")
      template(v-if="selectedDocument.id === 'stock-transfer'")
        .text-sm.font-semibold.neutral700--text Location
        div.mt-2(v-if="isLocked") {{ sourceCompany ? sourceCompany.name : '-' }}
        template(v-else)
          .mt-2
            hq-branch-warehouse-dropdown(:key="sourceCompanyDropdownKey" :value="sourceCompany" block @input="onRequestSourceCompanyChange")
      template(v-else)
        .text-sm.font-semibold.neutral700--text Supplier
        div.mt-2(v-if="isLocked") {{ supplier ? supplier.value : '-' }}
        div.mt-2.d-flex.align-center(v-else-if="isSupplierFilled")
          .text-sm.neutral700--text.text-truncate.mr-2 {{ supplier.value }}
          yz-badge.mr-2(v-if="activePic" color="neutral-300" text-color="neutral-500" :radius="4") {{ activePic.name }}
          v-menu(offset-y left)
            template(#activator="{ on, attrs }")
              v-btn(icon x-small v-bind="attrs" v-on="on")
                v-icon(size="18") mdi-dots-horizontal
            v-list(dense)
              v-list-item(@click="onOpenSelectPicDialog")
                v-list-item-title Select new PIC
              v-list-item(@click="onSwitchSupplier")
                v-list-item-title Switch supplier
        medication-sold-by-dropdown.mt-2(v-else v-model="supplier" text="supplier" show-add-seller height="36")
    v-col.pr-5(:cols="isQuotationOrStockTransfer ? 3 : 4")
      .text-sm.font-semibold.neutral700--text Send to
      div.mt-2(v-if="isLocked") {{ sendToDisplayText }}
      template(v-else)
        .mt-2(v-if="isHq || selectedDocument.id === 'stock-transfer'")
          hq-branch-warehouse-dropdown(v-model="sendTo" block)
        yz-dropdown.mt-2(v-else :value="company.name" :items="[company.name]" readonly placeholder="Select destination")
    v-col.pr-5(v-if="isQuotationOrStockTransfer" cols="3")
      .text-sm.font-semibold.neutral700--text {{ dateLabel }}
      div.mt-2(v-if="isLocked")
        span(v-if="dateFieldValue") {{ dateFieldValue | humanizeDate }}
      date-picker.mt-2(v-else v-model="dateFieldValue" :title="datePickerTitle" :height="36")
    v-col(:cols="isQuotationOrStockTransfer ? 3 : 4")
      .text-sm.font-semibold.neutral700--text Requested by
      div.mt-2(v-if="isLocked") {{ requestedBy || '-' }}
      yz-text-field.mt-2(v-else v-model="requestedBy" placeholder="Enter name")
  //- For purchase order or invoice
  template(v-if="selectedDocument.id === 'purchase-order' || selectedDocument.id === 'invoice'")
    v-divider.my-6
    v-row(no-gutters)
      v-col.pr-5(cols="4")
        .text-sm.font-semibold.neutral700--text Payment terms (optional)
        div.mt-2(v-if="isArchived || isCompleted")
          span(v-if="paymentTerms") {{ paymentTerms.value }} {{ 'day' | pluralize({count:paymentTerms.value}) }}
        payment-term-dropdown.mt-2(v-else v-model="paymentTerms")
      v-col.pr-5(cols="4")
        .text-sm.font-semibold.neutral700--text(v-if="selectedDocument.id === 'purchase-order'") Purchase order date
        .text-sm.font-semibold.neutral700--text(v-if="selectedDocument.id === 'invoice'") Invoice date
        div.mt-2(v-if="isArchived || isCompleted")
          span(v-if="recordDate") {{ recordDate | humanizeDate }}
        date-picker.mt-2(v-else v-model="recordDate" :title="`Select ${selectedDocument.id === 'purchase-order' ? 'purchase order' : 'invoice'} date`")
      v-col(cols="4")
        .text-sm.font-semibold.neutral700--text Due date (optional)
        div.mt-2(v-if="isArchived || isCompleted")
          span(v-if="dueDate") {{ dueDate | humanizeDate }}
        date-picker.mt-2(v-else v-model="dueDate" title="Select due date")
  //- Select PIC Dialog
  select-pic-dialog(v-model="showSelectPicDialog" :supplier-name="supplier.value" :contacts="supplierContacts" :current-pic-index="currentPicDialogIndex" @save="onSavePic")
</template>

<script>
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import MedicationSoldByDropdown from '@/components/clinic/purchasehub/MedicationSoldByDropdown.vue'
import PaymentTermDropdown from '@/components/clinic/purchasehub/PaymentTermDropdown.vue'
import DatePicker from '@/components/clinic/custom/DatePicker.vue'
import HqBranchWarehouseDropdown from '@/components/hq/inventory/HqBranchWarehouseDropdown.vue'
import SelectPicDialog from '@/components/clinic/purchasehub/document/SelectPicDialog.vue'

export default {
  components: {
    MedicationSoldByDropdown, DatePicker, PaymentTermDropdown, HqBranchWarehouseDropdown, SelectPicDialog
  },
  props: {
    selectedDocument: {
      type: Object,
      required: true
    },
    parentData: {
      type: Object,
      default: null
    },
    value: {
      type: Object,
      default: () => ({})
    }
  },
  mounted () {
    if (this.parentData) {
      this.supplier = this.parentData.supplier || {}
      this.quotationDate = this.parentData.record_date
      this.paymentTerms = this.parentData.payment_terms ? { value: this.parentData.payment_terms } : null
      this.recordDate = this.parentData.record_date
      this.dueDate = this.parentData.due_date
      this.sendTo = this.parentData.destination_company || null
      this.sourceCompany = this.parentData.source_company || null
      this.requestedBy = this.parentData.requested_by || null
    } else {
      this.quotationDate = this.getTodayDate()
      this.recordDate = this.getTodayDate()
    }
    if (!this.isHq && !this.sendTo) {
      this.sendTo = { id: this.company.id, name: this.company.name, type: 'branch' }
    }
    if (this.selectedDocument.id === 'stock-transfer' && !this.isHq && !this.sourceCompany) {
      this.sourceCompany = { id: this.company.id, name: this.company.name, type: 'branch' }
    }
  },
  data() {
    return {
      supplier: {},
      quotationDate: null,
      openSelectDateDialog: false,
      paymentTerms: null,
      recordDate: null,
      dueDate: null,
      sendTo: null,
      sourceCompany: null,
      requestedBy: null,
      showSelectPicDialog: false,
      selectedPicIndex: null,
      sourceCompanyDropdownKey: 0,
    }
  },
  watch: {
    supplier (newValue) {
      this.$emit('input', {
        ...this.value,
        supplier: newValue,
        selectedPicIndex: this.selectedPicIndex,
      })
    },
    selectedPicIndex (newValue) {
      this.$emit('input', {
        ...this.value,
        selectedPicIndex: newValue,
      })
    },
    quotationDate (newValue) {
      this.$emit('input', {
        ...this.value,
        quotationDate: newValue,
      })
    },
    paymentTerms (newValue) {
      this.$emit('input', {
        ...this.value,
        paymentTerms: newValue ? newValue.value : null,
      })
    },
    recordDate (newValue) {
      this.$emit('input', {
        ...this.value,
        recordDate: newValue,
      })
    },
    dueDate (newValue) {
      this.$emit('input', {
        ...this.value,
        dueDate: newValue,
      })
    },
    sendTo (newValue) {
      this.$emit('input', {
        ...this.value,
        sendTo: newValue,
      })
    },
    sourceCompany (newValue) {
      this.$emit('input', {
        ...this.value,
        sourceCompany: newValue,
      })
    },
    requestedBy (newValue) {
      this.$emit('input', {
        ...this.value,
        requestedBy: newValue,
      })
    }
  },
  computed: {
    ...mapGetters({ isHq: 'hq/getIsHq', company: 'getCompany' }),
    todayDate () {
      return DateTime.local().toISODate()
    },
    dateDetails () {
      return {
        date: this.date,
        title: 'Select quotation date',
      }
    },
    status () {
      return this.parentData?.request_status || this.parentData?.order_status
    },
    isArchived () {
      return this.status === 'archived'
    },
    isCompleted () {
      return this.status === 'completed'
    },
    isCreated () {
      return this.status === 'created'
    },
    isRequested () {
      return this.status === 'requested'
    },
    isRejected () {
      return this.status === 'rejected'
    },
    isLocked () {
      if (this.isArchived || this.isCompleted || this.isCreated) return true
      if (this.selectedDocument.id === 'stock-transfer' && (this.isRequested || this.isRejected)) return true
      return false
    },
    isQuotationOrStockTransfer () {
      return this.selectedDocument.id === 'quotation' || this.selectedDocument.id === 'stock-transfer'
    },
    dateLabel () {
      if (this.selectedDocument.id === 'quotation') return 'Quotation date'
      if (this.selectedDocument.id === 'stock-transfer') return 'Stock transfer request date'
      if (this.selectedDocument.id === 'purchase-order') return 'Purchase order date'
      return 'Invoice date'
    },
    datePickerTitle () {
      if (this.selectedDocument.id === 'quotation') return 'Select quotation date'
      if (this.selectedDocument.id === 'stock-transfer') return 'Select stock transfer request date'
      if (this.selectedDocument.id === 'purchase-order') return 'Select purchase order date'
      return 'Select invoice date'
    },
    dateFieldValue: {
      get () {
        if (this.selectedDocument.id === 'quotation' || this.selectedDocument.id === 'stock-transfer') return this.quotationDate
        return this.recordDate
      },
      set (val) {
        if (this.selectedDocument.id === 'quotation' || this.selectedDocument.id === 'stock-transfer') {
          this.quotationDate = val
        } else {
          this.recordDate = val
        }
      }
    },
    isSupplierFilled () {
      return !this.isLocked && this.supplier && Object.keys(this.supplier).length > 0
    },
    supplierContacts () {
      if (!this.supplier || !this.supplier.extra_data) return []
      const data = this.supplier.extra_data
      if (Array.isArray(data)) return data
      if (data.name || data.phone) return [{ name: data.name, phone: data.phone, default: true }]
      return []
    },
    activePic () {
      if (!this.supplierContacts.length) return null
      if (this.selectedPicIndex !== null && this.supplierContacts[this.selectedPicIndex]) {
        return this.supplierContacts[this.selectedPicIndex]
      }
      const defaultContact = this.supplierContacts.find(c => c.default)
      return defaultContact || this.supplierContacts[0]
    },
    currentPicDialogIndex () {
      if (this.selectedPicIndex !== null) return this.selectedPicIndex
      const idx = this.supplierContacts.findIndex(c => c.default)
      return idx >= 0 ? idx : 0
    },
    sendToDisplayText () {
      if (!this.sendTo) return '-'
      if (this.sendTo.name) return this.sendTo.name
      return '-'
    }
  },
  methods: {
    onRequestSourceCompanyChange (newValue) {
      this.$emit('request-source-company-change', newValue)
    },
    commitSourceCompany (newValue) {
      this.sourceCompany = newValue
    },
    revertSourceCompanySelection () {
      this.sourceCompanyDropdownKey += 1
    },
    onSwitchSupplier () {
      this.supplier = {}
      this.selectedPicIndex = null
    },
    onOpenSelectPicDialog () {
      this.showSelectPicDialog = true
    },
    onSavePic (picIndex) {
      this.selectedPicIndex = picIndex
    },
    getTodayDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`;
    }
  },
}
</script>

<style scoped>

</style>
